import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RouteFlowMap from './RouteFlowMap';
import { useConfigEditor } from 'shared/components/configPanel/useConfigEditor';
import UnsavedSectionPrompt, {
  SectionPrompt,
} from 'shared/components/configPanel/UnsavedSectionPrompt';
import { buttonClasses, onTealText } from 'styles/tailwindClassnames';
import { useIsMobile } from 'shared/hooks/useIsMobile';
import { FlowDirectionKey, Selection } from './flowMapTypes';
import { useAccountBundles } from './useFlowGraph';
import { useFlowSelection } from './configurationsTab/useFlowSelection';
import { useFlowStructure } from './configurationsTab/useFlowStructure';
import { useSharedBySite } from './configurationsTab/useSharedBySite';
import { useAccountApiKeys } from './configurationsTab/useAccountApiKeys';
import { useGeneralDraft } from './configurationsTab/useGeneralDraft';
import ConfirmationModal, {
  Confirmation,
} from './configurationsTab/ConfirmationModal';
import AccountPanel from './configurationsTab/AccountPanel';
import FlowPanel from './configurationsTab/FlowPanel';
import Alert from 'shared/components/ui/Alert';

// The Route's flow map and the panel for whatever is selected on it: an
// account (provider or destination) or an arrow between them.
const ConfigurationsTab = (props: any) => {
  const { route, routeProvider, token, currentMemberIsAdmin } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const providerId: string = routeProvider?.id || route?.provider?.id;
  const isMobile = useIsMobile();
  const destinations: any[] = React.useMemo(
    () => route?.destinations || [],
    [route?.destinations],
  );
  const accountIds = React.useMemo(
    () => [providerId, ...destinations.map((d: any) => d.id)].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [providerId, destinations.map((d: any) => d.id).join(',')],
  );

  // The parent fetches the provider integration itself — seed the bundle map
  // with it instead of re-fetching the same record.
  const { bundles, loading, failed, reload } = useAccountBundles(
    token,
    accountIds,
    providerId,
    routeProvider,
  );
  // Links out of this tab must use the id the page is ALREADY addressed by
  // (the parent's :route_id), never a re-derived one: deriving it flipped
  // between the route-record and provider-integration id spaces for records
  // reached through routing_rules, so a link pointed at a different spelling
  // of the same page. Route URLs carry the provider INTEGRATION id —
  // `route.id` on the merged connection object, NOT `default_route.id`.
  const routeUrlId: string = props.routeUrlId || route?.id;
  const { selection, select } = useFlowSelection();

  const selectedAccountId =
    selection.kind === 'account' ? selection.integrationId : undefined;
  const { arrows, nodes, edges, routeEnabled, needsSetup } = useFlowStructure({
    route,
    routeProvider,
    providerId,
    destinations,
    bundles,
    loading,
    reverseRoutesBySite: props.reverseRoutesBySite,
    providerCanReceive: props.providerCanReceive,
    destinationsWithRules: props.destinationsWithRules,
    reverseDestinationsWithRules: props.reverseDestinationsWithRules,
    destinationsWithDeviceFilters: props.destinationsWithDeviceFilters,
    onNeedsSetupChange: props.onNeedsSetupChange,
    selectedAccountId,
    selectedArrowId:
      selection.kind === 'flow'
        ? `${selection.destinationId}-${selection.direction}`
        : undefined,
    vertical: isMobile,
  });

  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  // The Save-or-discard question, shared with every configuration screen.
  const [sectionPrompt, setSectionPrompt] = useState<SectionPrompt | null>(
    null,
  );
  const [savingWebhook, setSavingWebhook] = useState(false);

  // ---- unsaved edits, stashed per account and PATCHed on Save -------------
  // Shared with every other configuration screen (destination page, wizard) so
  // "Save" always means "PATCH this one section".
  const editor = useConfigEditor({
    token,
    t,
    onSaved: (id: string) => reload(id),
  });
  const general = useGeneralDraft({
    token,
    selectedId: selectedAccountId,
    selectedBundle: selectedAccountId ? bundles[selectedAccountId] : undefined,
    fallbackTechValue: routeProvider?.type?.value,
    reload,
  });

  // Everything the user has stashed anywhere in this tab. The navigation guards
  // are leaving the whole selection behind, so they commit all of it rather than
  // just the section on screen.
  const savePendingEdits = async (): Promise<boolean> => {
    if (!(await general.save())) return false;
    const targets = Object.keys(editor.dirtyIds)
      .filter((id) => editor.dirtyIds[id])
      .map((id) => bundles[id])
      .filter(Boolean);
    for (const target of targets) {
      if (!(await editor.save(target))) return false;
    }
    return true;
  };
  const discardPendingEdits = () => {
    editor.discardAll();
    general.discard();
  };
  // The General draft is unsaved state too, even though it is not an action
  // configuration. The same Save-or-discard question a section switch asks —
  // offering only "discard" made the user cancel, save by hand, and come back
  // to click where they wanted to go.
  const hasUnsaved = editor.anyDirty || general.hasDraft;
  const askBefore = (proceed: () => void) =>
    setSectionPrompt({
      onSave: savePendingEdits,
      onDiscard: discardPendingEdits,
      proceed,
    });

  // Switching to a different node/arrow throws away whatever is stashed for the
  // current one, so ask first. Switching sections inside the same selection keeps
  // the same pending stash and never prompts.
  const guardedSelect = (next: Selection) => {
    const sameAccount =
      next.kind === 'account' &&
      selection.kind === 'account' &&
      next.integrationId === selection.integrationId;
    const sameFlow =
      next.kind === 'flow' &&
      selection.kind === 'flow' &&
      next.destinationId === selection.destinationId &&
      next.direction === selection.direction;
    if (hasUnsaved && !sameAccount && !sameFlow)
      return askBefore(() => select(next));
    select(next);
  };

  // Leaving the Route entirely deserves the same prompt as moving between
  // selections — unsaved edits would just vanish when the panel unmounts.
  const guardedNavigate = (to: string) => {
    if (hasUnsaved) return askBefore(() => navigate(to));
    navigate(to);
  };

  const sharedBySite = useSharedBySite({
    token,
    selectedDestId:
      selectedAccountId && selectedAccountId !== providerId
        ? selectedAccountId
        : undefined,
    routeId: route?.id,
  });
  const apiKeys = useAccountApiKeys({
    token,
    providerId,
    selectedAccountId,
    openSection: selection.kind === 'account' ? selection.section : undefined,
    arrows,
  });

  return (
    <div className="mt-8 px-0 pb-28 md:px-4">
      {failed && (
        <Alert
          status="warning"
          className="mb-4"
          action={
            <button
              type="button"
              className={buttonClasses.lightGreenSm}
              onClick={() => reload()}
            >
              {t('Retry')}
            </button>
          }
        >
          {t(
            "Some accounts in this Route couldn't be loaded, so parts of the map may be incomplete.",
          )}
        </Alert>
      )}
      {selection.kind === 'none' && (
        <p className={`${onTealText} mb-3`}>
          {loading
            ? t('Loading accounts…')
            : t('Select an account or a flow below to configure it.')}
        </p>
      )}
      <RouteFlowMap
        nodes={nodes}
        edges={edges}
        destinationCount={destinations.length}
        vertical={isMobile}
        routeStatus={route?.status}
        routeEnabled={routeEnabled}
        needsSetup={needsSetup}
        logsPath={routeUrlId ? `/routes/${routeUrlId}/logs` : undefined}
        onNavigateToLogs={guardedNavigate}
        onSelectAccount={(id: string) =>
          guardedSelect({ kind: 'account', integrationId: id })
        }
        onSelectFlow={(destinationId: string, direction: FlowDirectionKey) =>
          guardedSelect({ kind: 'flow', destinationId, direction })
        }
        onAddDestination={props.onAddDestination}
        onEnableRoute={() => props.onToggleRouteEnabled?.(true)}
        highlightAddDestination={!!props.highlightAddDestination}
        showClickHint={selection.kind === 'none' && !loading}
      />
      {confirmation && (
        <ConfirmationModal
          confirmation={confirmation}
          onClose={() => setConfirmation(null)}
        />
      )}
      <UnsavedSectionPrompt
        prompt={sectionPrompt}
        onClose={() => setSectionPrompt(null)}
      />
      {selection.kind === 'account' && (
        <AccountPanel
          selection={selection}
          select={select}
          bundles={bundles}
          destinations={destinations}
          arrows={arrows}
          providerId={providerId}
          routeProvider={routeProvider}
          routeEnabled={routeEnabled}
          token={token}
          currentMemberIsAdmin={currentMemberIsAdmin}
          editor={editor}
          general={general}
          apiKeys={apiKeys}
          sharedBySite={sharedBySite}
          confirm={setConfirmation}
          prompt={setSectionPrompt}
          savingWebhook={savingWebhook}
          setSavingWebhook={setSavingWebhook}
          guardedNavigate={guardedNavigate}
          providerSharedWith={props.providerSharedWith}
          onRemoveSite={props.onRemoveSite}
          onTestAction={props.onTestAction}
          testActionLoading={props.testActionLoading}
          testActionResponse={props.testActionResponse}
          onSaveWebhook={props.onSaveWebhook}
          onCancelWebhook={props.onCancelWebhook}
          webhookChangesMade={props.webhookChangesMade}
          onWebhookChangesMade={props.onWebhookChangesMade}
          organizations={props.organizations}
          onLoadMoreOrgs={props.onLoadMoreOrgs}
          onSearchOrgs={props.onSearchOrgs}
        />
      )}
      {selection.kind === 'flow' && (
        <FlowPanel
          selection={selection}
          select={select}
          route={route}
          providerId={providerId}
          destinations={destinations}
          bundles={bundles}
          arrows={arrows}
          routeEnabled={routeEnabled}
          confirm={setConfirmation}
          reverseRoutesBySite={props.reverseRoutesBySite}
          reverseRuleEditable={props.reverseRuleEditable}
          providerAlsoProvidesFor={props.providerAlsoProvidesFor}
          getRule={props.getRule}
          getReverseRule={props.getReverseRule}
          getSourceFilter={props.getSourceFilter}
          onOpenRules={props.onOpenRules}
          onToggleRouteEnabled={props.onToggleRouteEnabled}
          onEnableReverseFlow={props.onEnableReverseFlow}
          onDisableReverseFlow={props.onDisableReverseFlow}
        />
      )}
    </div>
  );
};

export default ConfigurationsTab;
